import {
  Component,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AdvancedMarker,
  APIProvider,
  InfoWindow,
  Map as VisGlMap,
  useMap,
} from '@vis.gl/react-google-maps';
import type { Marker } from '@googlemaps/markerclusterer';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { useIntl } from 'react-intl';

import clusterLarge from '../../assets/cluster-large.svg';
import clusterSmall from '../../assets/cluster-small.svg';
import pin from '../../assets/pin.svg';

import { breakpoints, googleMaps } from '../../config/config';
import { token } from '../../config/token';
import { MapUnavailableBox, MapWrapper } from './style';

export interface GoogleMapFeature {
  id: number;
  position: google.maps.LatLngLiteral;
}

export interface GoogleMapPopup {
  featureId: number;
  content: ReactNode;
}

export interface GoogleMapProps {
  features: GoogleMapFeature[];
  popup?: GoogleMapPopup | null;
  onFeatureClick?: (featureId: number) => void;
  onClusterClick?: (featureIds: number[]) => void;
  onPopupClose?: () => void;
  center?: google.maps.LatLngLiteral;
  zoom?: number;
}

const LATVIA_CENTER = { lat: 56.98, lng: 23.46 };
const DEFAULT_ZOOM = 8;
const PIN_HEIGHT = 28;
const CLUSTER_LARGE_THRESHOLD = 10;
const POPUP_ANCHOR_Y_RATIO = 0.8;
const POPUP_ANCHOR_Y_RATIO_MOBILE = 0.64;
const POPUP_TOP_MARGIN = 28;

const MapUnavailable = ({ message }: { message: string }) => (
  <MapUnavailableBox>{message}</MapUnavailableBox>
);

class MapErrorBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

const useGoogleMapsAuthFailure = () => {
  const [authFailed, setAuthFailed] = useState(false);

  useEffect(() => {
    const previous = window.gm_authFailure;
    window.gm_authFailure = () => {
      setAuthFailed(true);
      previous?.();
    };
    return () => {
      window.gm_authFailure = previous;
    };
  }, []);

  return authFailed;
};

const makeClusterIcon = (count: number): HTMLElement => {
  const wrapper = document.createElement('div');
  wrapper.style.display = 'grid';
  wrapper.style.placeItems = 'center';

  const img = document.createElement('img');
  img.src = count >= CLUSTER_LARGE_THRESHOLD ? clusterLarge : clusterSmall;
  img.alt = '';
  img.style.gridArea = '1 / 1';

  const label = document.createElement('span');
  label.textContent = String(count);
  label.style.gridArea = '1 / 1';
  label.style.color = token.white;
  label.style.fontWeight = '600';

  wrapper.appendChild(img);
  wrapper.appendChild(label);
  return wrapper;
};

interface FeatureMarkerProps {
  feature: GoogleMapFeature;
  onFeatureClick?: (featureId: number) => void;
  onMarkerRef: (marker: Marker | null, featureId: number) => void;
}

const FeatureMarker = memo(
  ({ feature, onFeatureClick, onMarkerRef }: FeatureMarkerProps) => {
    const markerRef = useCallback(
      (marker: Marker | null) => onMarkerRef(marker, feature.id),
      [onMarkerRef, feature.id]
    );
    const handleClick = useCallback(
      () => onFeatureClick?.(feature.id),
      [onFeatureClick, feature.id]
    );

    return (
      <AdvancedMarker
        position={feature.position}
        ref={markerRef}
        onClick={handleClick}
      >
        <img src={pin} alt="" />
      </AdvancedMarker>
    );
  }
);

type ClusteredMarkersProps = Pick<
  GoogleMapProps,
  'features' | 'onFeatureClick' | 'onClusterClick'
>;

const ClusteredMarkers = memo(
  ({ features, onFeatureClick, onClusterClick }: ClusteredMarkersProps) => {
    const map = useMap();
    const [markers, setMarkers] = useState<Record<number, Marker>>({});
    const clustererRef = useRef<MarkerClusterer | null>(null);

    const onClusterClickRef = useRef(onClusterClick);
    onClusterClickRef.current = onClusterClick;

    useEffect(() => {
      if (!map) return;
      clustererRef.current = new MarkerClusterer({
        map,
        onClusterClick: (_event, cluster) => {
          const ids = (cluster.markers ?? [])
            .map((marker) =>
              Number(
                (marker as google.maps.marker.AdvancedMarkerElement).dataset
                  .featureId
              )
            )
            .filter((id): id is number => Number.isFinite(id));
          onClusterClickRef.current?.(ids);
        },
        renderer: {
          render: ({ count, position }) =>
            new google.maps.marker.AdvancedMarkerElement({
              position,
              content: makeClusterIcon(count),
              zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
            }),
        },
      });
      return () => {
        clustererRef.current?.clearMarkers(true);
        clustererRef.current = null;
      };
    }, [map]);

    useEffect(() => {
      const clusterer = clustererRef.current;
      if (!clusterer) return;
      clusterer.clearMarkers(true);
      clusterer.addMarkers(Object.values(markers));
    }, [markers]);

    const setMarkerRef = useCallback((marker: Marker | null, id: number) => {
      setMarkers((prev) => {
        if (marker) {
          if (prev[id] === marker) return prev;
          (
            marker as google.maps.marker.AdvancedMarkerElement
          ).dataset.featureId = String(id);
          return { ...prev, [id]: marker };
        }
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }, []);

    return (
      <>
        {features.map((feature) => (
          <FeatureMarker
            key={feature.id}
            feature={feature}
            onFeatureClick={onFeatureClick}
            onMarkerRef={setMarkerRef}
          />
        ))}
      </>
    );
  }
);

const GoogleMapInner = ({
  features,
  popup,
  onFeatureClick,
  onClusterClick,
  onPopupClose,
  center,
  zoom,
}: GoogleMapProps) => {
  const map = useMap();

  const popupPosition = popup
    ? features.find((f) => f.id === popup.featureId)?.position
    : undefined;

  const panPopupIntoView = useMemo(() => {
    if (!map || !popupPosition) return undefined;
    return (content: HTMLDivElement | null) => {
      if (!content) return;
      let attempts = 0;

      const measureAndPan = () => {
        const popupHeight = content.offsetHeight;
        if (!popupHeight && attempts++ < 30) {
          requestAnimationFrame(measureAndPan);
          return;
        }

        const h = map.getDiv().clientHeight;
        if (!h) return;

        const anchorRatio =
          window.innerWidth < breakpoints.sm
            ? POPUP_ANCHOR_Y_RATIO_MOBILE
            : POPUP_ANCHOR_Y_RATIO;
        const minPinY = popupHeight + PIN_HEIGHT + POPUP_TOP_MARGIN;
        const pinY = Math.min(Math.max(h * anchorRatio, minPinY), h - 16);

        map.panTo(popupPosition);
        map.panBy(0, Math.round(h / 2 - pinY));
      };

      requestAnimationFrame(measureAndPan);
    };
  }, [map, popupPosition]);

  return (
    <MapWrapper>
      <VisGlMap
        mapId={googleMaps.mapId}
        defaultCenter={center ?? LATVIA_CENTER}
        defaultZoom={zoom ?? DEFAULT_ZOOM}
        gestureHandling="greedy"
        mapTypeControl={false}
        streetViewControl={false}
        fullscreenControl={false}
        clickableIcons={false}
        style={{ height: '100%', width: '100%' }}
        onClick={() => onPopupClose?.()}
      >
        <ClusteredMarkers
          features={features}
          onFeatureClick={onFeatureClick}
          onClusterClick={onClusterClick}
        />
        {popup && popupPosition && (
          <InfoWindow
            key={popup.featureId}
            position={popupPosition}
            pixelOffset={[0, -PIN_HEIGHT]}
            onClose={onPopupClose}
            headerDisabled
            disableAutoPan
          >
            <div ref={panPopupIntoView}>{popup.content}</div>
          </InfoWindow>
        )}
      </VisGlMap>
    </MapWrapper>
  );
};

export const GoogleMap = (props: GoogleMapProps) => {
  const intl = useIntl();
  const authFailed = useGoogleMapsAuthFailure();

  useEffect(() => {
    if (!googleMaps.apiKey) {
      console.warn(
        'Google Maps API key is not configured — set googleMapsApiKey in runtime-config.js or REACT_APP_GOOGLE_MAPS_API_KEY in .env'
      );
    }
  }, []);

  const unavailable = (
    <MapUnavailable message={intl.formatMessage({ id: 'map.unavailable' })} />
  );

  if (!googleMaps.apiKey || authFailed) return unavailable;

  return (
    <MapErrorBoundary fallback={unavailable}>
      <APIProvider apiKey={googleMaps.apiKey}>
        <GoogleMapInner {...props} />
      </APIProvider>
    </MapErrorBoundary>
  );
};
